import React, {
	FC,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Card, Spin, Table, Tabs, Tooltip } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';
import { clearNavbarState, updateNavbarState } from '../../actions/navbar_actions';
import { EmptyState } from '../empty_state/EmptyState';
import {
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
} from '../../utils/DataAccessUtils';
import { isThirdPartySupplier } from '../../utils/AuthUtils';
import { withUserType } from '../withUserType';
import {
	formatLeakRatePercent,
	formatPounds,
	getApplianceCategoryLabel,
	getProgrammeComplianceBand,
	getProgrammeThresholdPercent,
	getRefrigerantComplianceStatusLabel,
	getRefrigerantProgramme,
	getRefrigerantProgrammeKeysForAsset,
	getRefrigerantProgrammeStandardsLine,
	REFRIGERANT_PROGRAMMES,
	REFRIGERANT_SERVICING_RECORD_TC,
} from '../../utils/RefrigerantUtils';
import {
	refrigerantEventsRestCrudThunks,
	retrieveRefrigerantAssetSummary,
} from '../../thunks/refrigerant_events_thunks';
import { assetsRestCrudThunks } from '../../thunks/assets_thunks';
import getRefrigerantEventLedgerColumns, {
	getRefrigerantEventLedgerMinTableWidth,
} from '../refrigerant/RefrigerantEventLedgerColumns';
import { getBackLinkStates } from '../../utils/HistoryUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { downloadServicingRecordCsv } from '../../utils/RefrigerantServicingRecordExport';
import './RefrigerantServicingRecordPage.less';

// The whole ledger, unpaged: a 608 record is a document, and a document with a page 2 the reader
// cannot see is not evidence of anything. Same bound as the appliance's own ledger tab.
const LEDGER_PAGE_SIZE = 200;

/**
 * The printable refrigerant servicing record for one appliance.
 *
 * A DOCUMENT, not a screen: everything on it is either a recorded fact about the equipment or a
 * figure the server derived from the append-only ledger, and nothing is editable. The one
 * client-side derivation is the "generated" date in the byline, which describes when the paper was
 * produced rather than anything about the appliance.
 *
 * The per-event certification is printed from each event's own SNAPSHOT (`certificationType` /
 * `certificationNumber`), never from the technician's current contact record. That is the whole
 * point of snapshotting it: a cert renewed or corrected next year must not silently rewrite what
 * last year's record attests to.
 *
 * The three programme tabs all render the SAME document against DIFFERENT standards, driven by
 * `REFRIGERANT_PROGRAMME_DEFINITIONS`. The appliance's facts and its service events do not change
 * between programmes; the threshold it is measured against, the resulting status, the standards line
 * and the footer all do. EPA 608's threshold and band come off the server's summary, because that is
 * what the server actually banded against; the voluntary and state programmes are derived on read
 * against their own numbers.
 */
const RefrigerantServicingRecordPage: FC<any> = ({
	asset,
	match,
	location,
	userType,
	currentUser,
	companyConfig,
	events,
	fetchAsset,
	fetchSummary,
	fetchEvents,
	updateNavbar,
	clearNavbar,
}) => {
	const assetId = nullSafeGet('params.id', match);
	const [summary, setSummary] = useState(null);
	const [loading, setLoading] = useState(true);
	// A printable 608 document must never be generated from a failed read: an empty record here does not
	// mean "no servicing took place", it means we could not find out.
	const [loadFailed, setLoadFailed] = useState(false);
	const [activeTab, setActiveTab] = useState(REFRIGERANT_PROGRAMMES.EPA_608);

	/**
	 * The navbar back-link, and its teardown.
	 *
	 * `useLayoutEffect`, not `useEffect`, and the reason is the teardown rather than the setup. A
	 * passive cleanup on an UNMOUNTING route is flushed after the incoming route has already mounted —
	 * React runs `componentDidMount` in the layout phase and passive destroys in the phase after it —
	 * so `clearNavbar()` landed on top of the back-link the asset detail page had just set for itself,
	 * and coming back from this record left the header with no back link at all. A layout cleanup runs
	 * in the mutation phase, before anything in the incoming tree mounts, which is the order this
	 * hand-off has always assumed.
	 *
	 * Where it points is read off the URL rather than hardcoded. This record is reachable from an
	 * appliance's Refrigerant Tracking tab and from a work order's, and each of those was itself
	 * reached from somewhere — most often the Refrigerant Tracking index, whose link carries the
	 * filters and the label "Back to Refrigerant Tracking". Returning to a hardcoded path threw that
	 * chain away and dropped the user on a bare asset page. The fallback stays for a record opened
	 * cold, by direct URL, where there is no chain to preserve.
	 */
	useLayoutEffect(() => {
		const defaultBackLinkTo = `/${userType}/assets/detail/${assetId}/refrigerantTracking`;
		const { backLinkText, backLinkTo } = getBackLinkStates(location, defaultBackLinkTo) as any;
		updateNavbar(backLinkText || 'Back to Asset', backLinkTo || defaultBackLinkTo);
		return () => clearNavbar();
	}, [assetId, clearNavbar, location, updateNavbar, userType]);

	// The record on screen belongs to exactly one appliance. Navigating from one servicing record
	// straight to another must not leave the PREVIOUS appliance's document rendered under the new URL:
	// the only spinner path is `loading && !summary`, so a retained summary keeps a printable — and
	// downloadable — compliance document for the wrong asset live until the new requests land.
	const [summaryAssetId, setSummaryAssetId] = useState(assetId);
	if (summaryAssetId !== assetId) {
		setSummaryAssetId(assetId);
		setSummary(null);
		setLoadFailed(false);
	}

	// Bumped per refresh so a response that lost a race cannot overwrite the current appliance's record.
	const requestRef = useRef(0);

	// The asset the CURRENT request is for, readable from inside an older request's callback. A stale
	// `.then` closes over its own `assetId`, so it cannot otherwise know which appliance is on screen
	// by the time it runs.
	const assetIdRef = useRef(assetId);
	assetIdRef.current = assetId;

	// Which appliance the ledger currently in `REFRIGERANT_SERVICING_RECORD_TC` was fetched for.
	// Unlike the summary, the ledger is NOT component state: `fetchEvents` dispatches into a shared
	// redux collection, so a late response for appliance A overwrites the rows on screen for B no
	// matter what this component does with its own state. The guard below therefore has to do two
	// things a plain request-id check cannot — mark the ledger as belonging to another appliance so it
	// is not rendered, and re-issue the read so the shared collection converges back.
	//
	// A per-asset target collection would be the cleaner fix, but `getRecordsForTargetCollection`
	// throws on any name not declared up front in `refrigerant_events_reducer`, so dynamic names would
	// need that reducer reworked first.
	const [ledgerAssetId, setLedgerAssetId] = useState(null);

	useEffect(() => {
		const requestId = requestRef.current + 1;
		requestRef.current = requestId;
		setLoading(true);
		setLoadFailed(false);
		Promise.all([
			fetchAsset(userType, assetId),
			fetchSummary(userType, assetId).then((result) => {
				if (requestRef.current === requestId) setSummary(result);
			}),
			fetchEvents(userType, assetId).then(() => {
				if (requestRef.current === requestId) {
					setLedgerAssetId(assetId);
					return;
				}
				// This response lost the race and has just overwritten the shared collection with a
				// PREVIOUS appliance's ledger. Pull the current one back in; `setLedgerAssetId` above
				// keeps the record hidden until it lands.
				setLedgerAssetId(assetId);
				fetchEvents(userType, assetIdRef.current).then(() => setLedgerAssetId(assetIdRef.current));
			}),
		])
			// `.finally` does not consume a rejection. Without this catch a 401 or a network failure left an
			// unhandled rejection AND rendered the document with every figure as `--` and an empty event
			// table — a compliance record asserting that nothing was serviced, produced from reads that
			// never returned. Print and Download were both live on it.
			.catch(() => {
				if (requestRef.current === requestId) setLoadFailed(true);
			})
			.finally(() => {
				if (requestRef.current === requestId) setLoading(false);
			});
	}, [assetId, fetchAsset, fetchEvents, fetchSummary, userType]);

	// Empty, not the collection's contents, while the rows in the shared collection belong to another
	// appliance. Rendering them would put appliance A's servicing history under appliance B's identity
	// block on a printable, downloadable compliance document — worse than rendering nothing, because
	// the reader has no way to tell. The `ledgerIsCurrent` gate below turns that into the spinner.
	const ledgerIsCurrent = ledgerAssetId === assetId;

	// The same question for the APPLIANCE, and the reason it has to be asked: the programme tabs are
	// derived from the asset, and `getEntityById` answers `{}` until the read lands. An appliance that
	// has not arrived therefore looks exactly like one outside California — so a reader who had CARB
	// selected and moved to another record would watch the document re-render as EPA 608 and then jump
	// back, with Print and Download live on it throughout. Spin instead of publishing a programme the
	// reader did not choose. Compared as strings because the route id is one and the record's is not.
	const assetIsCurrent = String(nullSafeGet('id', asset)) === String(assetId);
	const ledger = useMemo(
		() =>
			ledgerIsCurrent ? getRecordsForTargetCollection(events, REFRIGERANT_SERVICING_RECORD_TC) : [],
		[events, ledgerIsCurrent]
	);

	const columns = useMemo(
		// Certification on, avatars off: this is printed. An avatar is a UI affordance that carries no
		// information onto paper, and the cert number is the field that makes the row evidence.
		() =>
			getRefrigerantEventLedgerColumns({
				userType,
				showAsset: false,
				showCertification: true,
				printable: true,
			}),
		[userType]
	);

	// Memoised alongside the columns it is derived from — see the same note on the appliance's own
	// ledger. Built inline it would hand antd's Table a new `scroll` object on every render.
	const ledgerScroll = useMemo(
		() => ({ x: getRefrigerantEventLedgerMinTableWidth(columns) }),
		[columns]
	);

	/**
	 * The record's identity block. `assetTags` first, `assetNumber` second: the design labels this
	 * "Asset Tag", and a site's own tag is what a tech reads off the equipment — the asset number is
	 * OpenWrench's identifier and only stands in when no tag was recorded.
	 */
	const assetTag =
		nullSafeGetOrElse('assetTags.0', asset, null) || nullSafeGet('assetNumber', asset) || '--';

	const locationLabel = useMemo(() => {
		const storeId = nullSafeGet('location.storeId', asset);
		const name = nullSafeGet('location.name', asset);
		if (!name) return '--';
		return storeId ? `Store #${storeId} - ${name}` : name;
	}, [asset]);

	const identityFields = useMemo(
		() => [
			{ label: 'Asset', value: nullSafeGetOrElse('name', asset, '--') },
			{ label: 'Asset Tag', value: assetTag },
			{ label: 'Location', value: locationLabel },
			{
				label: 'Category',
				value: nullSafeGet('applianceCategory', summary)
					? getApplianceCategoryLabel(nullSafeGet('applianceCategory', summary))
					: '--',
			},
			{ label: 'Refrigerant', value: nullSafeGetOrElse('refrigerantTypeCode', summary, '--') },
		],
		[asset, assetTag, locationLabel, summary]
	);

	/**
	 * The programmes this appliance can actually produce a record for. CARB RMP is California's, so it
	 * is absent unless the appliance sits there — see `getRefrigerantProgrammeKeysForAsset`.
	 */
	const programmeKeys = useMemo(() => getRefrigerantProgrammeKeysForAsset(asset), [asset]);

	/**
	 * The tab that is actually in force, which is not always the one the user last clicked. The
	 * selection outlives the appliance: this page is mounted once and re-fetches when the route's asset
	 * id changes, and it is reachable straight from another appliance's record — so a reader who picked
	 * CARB on a Los Angeles store and moved to an Ohio one would otherwise be printing a California
	 * compliance record for an appliance that is not in the programme. Falling back to EPA 608, the
	 * federal programme every tracked appliance is in, is the one answer that is never wrong.
	 *
	 * Everything downstream — the standards line, the threshold, the band, the footer and the CSV —
	 * resolves from this rather than from `activeTab`, so the document and the tab strip cannot
	 * disagree about which programme is being attested to.
	 */
	const programmeKey =
		programmeKeys.indexOf(activeTab) > -1 ? activeTab : REFRIGERANT_PROGRAMMES.EPA_608;

	const programme = useMemo(() => getRefrigerantProgramme(programmeKey), [programmeKey]);

	/**
	 * The threshold and band are resolved PER PROGRAMME, which is the whole point of the tabs: the
	 * same appliance sits at 20% under EPA 608 and 15% under GreenChill, so a record that printed one
	 * number under all three headings would be wrong on two of them.
	 */
	const programmeThresholdPercent = useMemo(
		() =>
			getProgrammeThresholdPercent(
				programmeKey,
				nullSafeGet('applianceCategory', summary),
				summary
			),
		[programmeKey, summary]
	);

	const programmeBand = useMemo(
		() => getProgrammeComplianceBand(programmeKey, summary, programmeThresholdPercent),
		[programmeKey, programmeThresholdPercent, summary]
	);

	const complianceFields = useMemo(
		() => [
			{ label: 'Full charge', value: formatPounds(nullSafeGetWithZero('fullChargeLb', summary)) },
			{
				label: '12-month leak rate',
				value: formatLeakRatePercent(nullSafeGetWithZero('leakRatePercent', summary)),
			},
			{
				label: programme.thresholdLabel,
				value:
					programmeThresholdPercent === null || programmeThresholdPercent === undefined
						? '--'
						: `${programmeThresholdPercent}%`,
			},
			{ label: 'Status', value: getRefrigerantComplianceStatusLabel(programmeBand) },
		],
		[programme, programmeBand, programmeThresholdPercent, summary]
	);

	const companyName = nullSafeGetOrElse(
		'buyerCompany.name',
		currentUser,
		nullSafeGetOrElse('name', companyConfig, '')
	);

	const handlePrint = useCallback(() => window.print(), []);

	const handleDownload = useCallback(
		() =>
			downloadServicingRecordCsv({
				asset,
				assetTag,
				locationLabel,
				summary,
				events: ledger,
				companyName,
				programmeKey,
				thresholdPercent: programmeThresholdPercent,
				band: programmeBand,
			}),
		[
			programmeKey,
			asset,
			assetTag,
			companyName,
			ledger,
			locationLabel,
			programmeBand,
			programmeThresholdPercent,
			summary,
		]
	);

	const renderRecord = () => (
		<Card className="refrigerant-servicing-record__sheet" bodyStyle={{ padding: 32 }}>
			<div className="flex flex-row flex-wrap items-start justify-between gap-4">
				<div className="flex flex-col">
					<span className="h3medium">Refrigerant servicing record</span>
					<span className="footnoteregular text-character-secondary">
						{[programme.name, `generated ${moment().format('MMM DD, YYYY')}`, companyName]
							.filter(Boolean)
							.join(' · ')}
					</span>
				</div>
				{/* Who produced the record. Present on the paper because "generated by" is part of what
				    makes a compliance document attributable. */}
				<div className="refrigerant-servicing-record__author">
					<ContactAvatar size="large" contact={currentUser} />
				</div>
			</div>

			<div className="refrigerant-servicing-record__facts">
				{identityFields.map((field) => (
					<div key={field.label} className="flex flex-col">
						<span className="footnoteregular text-character-secondary">{field.label}</span>
						<span className="bodymedium text-character-primary">{field.value}</span>
					</div>
				))}
			</div>

			{/* The programme's own scope and thresholds, so the reader can see what the status below was
			    measured against without having to know the programme by heart. */}
			<div className="refrigerant-servicing-record__standards footnoteregular text-character-secondary">
				{getRefrigerantProgrammeStandardsLine(programmeKey)}
			</div>

			<div className="refrigerant-servicing-record__facts">
				{complianceFields.map((field) => (
					<div key={field.label} className="flex flex-col">
						<span className="footnoteregular text-character-secondary">{field.label}</span>
						<span className="bodymedium text-character-primary">{field.value}</span>
					</div>
				))}
			</div>

			<div className="h5medium" style={{ margin: '32px 0 8px' }}>
				Service Events
			</div>
			{ledger.length > 0 ? (
				<Table
					dataSource={ledger}
					columns={columns}
					rowKey={(record: any) => record.id}
					pagination={false}
					// A number, not `max-content`: the latter sized the table to the longest note on one
					// unwrapped line, which ran the record off the side of its own sheet. The floor is built
					// to fit inside the 1100px sheet, so Notes wraps within the measure rather than setting it.
					scroll={ledgerScroll}
				/>
			) : (
				<EmptyState
					graphic={null}
					headline={'No servicing recorded for this appliance yet.'}
					body={
						<div style={{ textAlign: 'center', maxWidth: 440 }}>
							A servicing record with no events is still a valid answer to an audit — it attests
							that nothing has been logged against this appliance.
						</div>
					}
				/>
			)}

			<div
				className="footnoteregular text-character-secondary"
				style={{ marginTop: 24, lineHeight: 1.6 }}
			>
				{programme.footer}
			</div>
		</Card>
	);

	// Matches the gate on the link that leads here (AssetRefrigerantTrackingPage). The link is hidden
	// for a third-party supplier, but the route is reachable by URL, and reads that fail closed onto an
	// empty ledger would render as a document attesting that no servicing took place.
	if (isThirdPartySupplier(currentUser)) {
		return (
			<EmptyState
				headline="Servicing record unavailable"
				body="The refrigerant servicing history for this appliance is held by the customer and is not shared with contractors."
			/>
		);
	}

	if (loadFailed) {
		return (
			<EmptyState
				headline="We could not load this servicing record"
				body="The refrigerant summary or the event ledger failed to load, so this record cannot be produced. Reload the page to try again."
			/>
		);
	}

	// `!ledgerIsCurrent` alongside the summary check: a stale ledger read that landed last leaves the
	// shared collection holding another appliance's rows, and the re-issued read is already in flight.
	// Spin until it lands rather than publish a record whose event table is empty for a reason the
	// reader cannot see. `!assetIsCurrent` is the same rule for the appliance itself — see above.
	//
	// After the `loadFailed` branch, so a read that never returns surfaces as the error state rather
	// than as a spinner that never stops.
	if ((loading && !summary) || !ledgerIsCurrent || !assetIsCurrent) {
		return (
			<div className="flex flex-row justify-center" style={{ padding: 48 }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="refrigerant-servicing-record flex flex-col p-4">
			<div className="refrigerant-servicing-record__toolbar flex flex-row items-center justify-between">
				{/* `programmeKey`, not `activeTab`: a selection the appliance no longer offers has already
				    fallen back above, and the strip has to show the tab the document below is actually
				    written against. */}
				<Tabs activeKey={programmeKey} onChange={setActiveTab}>
					{programmeKeys.map((availableProgrammeKey) => (
						<Tabs.TabPane
							tab={getRefrigerantProgramme(availableProgrammeKey).tabLabel}
							key={availableProgrammeKey}
						/>
					))}
				</Tabs>
				<div className="flex flex-row items-center gap-2">
					<Tooltip title="Print this record">
						<Button icon={<PrinterOutlined />} onClick={handlePrint} />
					</Tooltip>
					<Tooltip title="Download the service events as CSV">
						<Button icon={<DownloadOutlined />} onClick={handleDownload} />
					</Tooltip>
				</div>
			</div>

			{renderRecord()}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	asset: getEntityById(ownProps.match.params.id, state.assets),
	events: state.refrigerant_events,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

/**
 * `(dispatch)` ONLY, with the role passed in at call time — see the same note on
 * `AssetRefrigerantTrackingPage`, which had the bug this shape prevents. react-redux recomputes the
 * dispatch props on every ownProps change when this function declares `ownProps`, and the read effect
 * below depends on their identities; ownProps churn on any re-render of the route tree above, so the
 * effect would re-fetch in a cycle that keeps feeding itself.
 */
const mapDispatchToProps = (dispatch) => ({
	fetchAsset: (userType, assetId) => dispatch(assetsRestCrudThunks(userType).readOne(assetId)),
	fetchSummary: (userType, assetId) => dispatch(retrieveRefrigerantAssetSummary(userType)(assetId)),
	fetchEvents: (userType, assetId) =>
		dispatch(
			refrigerantEventsRestCrudThunks(userType).read(
				{ assetId },
				REFRIGERANT_SERVICING_RECORD_TC,
				{ current: 1, pageSize: LEDGER_PAGE_SIZE },
				// Oldest first, unlike the working ledger. A servicing record is read as a narrative of
				// what happened to the appliance, and a narrative runs forwards.
				{ sort_by: 'occurredAt', order: 'ascend' }
			)
		),
	updateNavbar: (backlinkTitle, backlinkTo) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, null)),
	clearNavbar: () => dispatch(clearNavbarState()),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(RefrigerantServicingRecordPage as any)
	) as any
);
